import {useCallback, useState} from 'react';

import type {
    BreakoutParticipants,
    BreakoutRoomNames,
    BreakoutsEditPanelSave,
    ParticipantID,
} from '../types';
import {useBreakoutChangeParticipantRoom} from './useBreakoutChangeParticipantRoom';
import {useBreakoutParticipantDrag} from './useBreakoutParticipantDrag';
import {useBreakoutRemoveRoom} from './useBreakoutRemoveRoom';
import {useBreakoutRoomNameChange} from './useBreakoutRoomNameChange';
import {useBreakoutAddRoom} from './useBreakoutAddRoom';
import {useBreakoutShuffleParticipants} from './useBreakoutShuffleParticipants';
import {useBreakoutEditDiff} from './useBreakoutEditDiff';

export const useBreakoutEdit = ({
    currentParticipants,
    breakoutRoomsNames: breakoutRoomsNamesProp,
    myIdentity,
    onSave,
}: {
    currentParticipants: BreakoutParticipants;
    breakoutRoomsNames: BreakoutRoomNames;
    myIdentity?: ParticipantID;
    onSave: (data: BreakoutsEditPanelSave) => void;
}) => {
    const [participants, setParticipants] = useState(currentParticipants);
    const [breakoutRoomsNames, setBreakoutRoomsNames] = useState(
        breakoutRoomsNamesProp,
    );
    const [isDragging, setIsDragging] = useState(false);

    const changeParticipantRoom = useBreakoutChangeParticipantRoom({
        participants,
        setParticipants,
    });

    const {
        targetRoomId: currentDragTarget,
        onParticipantDragging,
        onParticipantDrag,
        getBreakoutRoomRefCallback,
    } = useBreakoutParticipantDrag({
        participants,
        changeParticipantRoom,
        setIsDragging,
    });

    const onRemoveRoom = useBreakoutRemoveRoom({setParticipants});

    const {handleBreakoutRoomNameChange} = useBreakoutRoomNameChange({
        setParticipants,
        participants,
        breakoutRoomsNames,
        setBreakoutRoomsNames,
    });

    const onAddRoom = useBreakoutAddRoom({
        setParticipants,
        existingBreakoutNames: Object.values(breakoutRoomsNames),
    });

    const {doShuffle, isShuffling} = useBreakoutShuffleParticipants({
        setParticipants,
        myIdentity,
    });

    const reduceToIdentities = (participants: BreakoutParticipants) =>
        Array.from(participants).reduce((acc, [roomId, roomParticipants]) => {
            acc.set(
                roomId,
                roomParticipants.map(p => p.uuid),
            );
            return acc;
        }, new Map());

    const resolveEdit = useBreakoutEditDiff({
        liveBreakoutNames: breakoutRoomsNamesProp,
        localBreakoutNames: breakoutRoomsNames,
        liveParticipants: reduceToIdentities(currentParticipants),
        localParticipants: reduceToIdentities(participants),
    });

    const doSave = useCallback(() => {
        const updateActions = resolveEdit();
        onSave(updateActions);
    }, [resolveEdit, onSave]);

    return {
        participants,
        breakoutRoomsNames,
        isDragging,
        currentDragTarget,
        onParticipantDragging,
        onParticipantDrag,
        getBreakoutRoomRefCallback,
        onRemoveRoom,
        handleBreakoutRoomNameChange,
        onAddRoom,
        doShuffle,
        isShuffling,
        doSave,
        changeParticipantRoom,
    };
};
